import { useEffect, useRef, useState } from "react";
import gwcLogo from "../images/GWC_RGB_600px.jpg";
import gwcSymbol from "../images/GWC_Symbol_RGB_700px.png";
import questionMarkLogo from "../images/GWC_QuestionMarkLogo.png";
import { buildSubmissionRequest, fetchPublicConfig, submitApplication as submitApplicationRequest } from "./lib/api";
import { useInterviewRecorder } from "./hooks/useInterviewRecorder";
import type { ApplicantProfile, PublicConfig, RecordedAnswer } from "./types";

type Stage = "loading" | "profile" | "record" | "review" | "uploading" | "done" | "error";
type QuestionPhase = "intro" | "prep" | "recording" | "decision";

const PREPARATION_SECONDS = 60;
const DECISION_SECONDS = 30;
const MAX_RECORDING_SECONDS = 120;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const initialProfile: ApplicantProfile = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  location: "",
  consentAccepted: false
};

function formatSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function formatMegabytes(sizeBytes: number): string {
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readInvitationToken(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("invite") ?? params.get("token") ?? "";
}

export default function App() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [stage, setStage] = useState<Stage>("loading");
  const [profile, setProfile] = useState<ApplicantProfile>(initialProfile);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionPhase, setQuestionPhase] = useState<QuestionPhase>("intro");
  const [phaseSecondsLeft, setPhaseSecondsLeft] = useState(PREPARATION_SECONDS);
  const [attemptsByQuestion, setAttemptsByQuestion] = useState<Record<string, number>>({});
  const [recordedAnswers, setRecordedAnswers] = useState<Record<string, RecordedAnswer>>({});
  const [reviewVideoUrl, setReviewVideoUrl] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const [turnstileLoadFailed, setTurnstileLoadFailed] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Loading interview configuration...");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const phaseEndTimeRef = useRef<number | null>(null);
  const recorder = useInterviewRecorder();

  function resetTurnstileWidget() {
    if (turnstileWidgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      } catch {
        // widget may already be detached if the container unmounted
      }
    }
    turnstileWidgetIdRef.current = null;
    setTurnstileToken("");
    setTurnstileRenderKey((k) => k + 1);
  }

  useEffect(() => {
    const invitationToken = readInvitationToken();

    if (!invitationToken) {
      setErrorMessage("This interview link is incomplete or invalid.");
      setStatusMessage("Interview link required.");
      setStage("error");
      return;
    }

    fetchPublicConfig(invitationToken)
      .then((loadedConfig) => {
        setConfig(loadedConfig);
        setProfile({
          ...loadedConfig.applicant,
          consentAccepted: false
        });
        setStage("profile");
        setStatusMessage("Confirm your details to begin.");
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Unable to load configuration.";
        setErrorMessage(message);
        setStatusMessage("Configuration failed to load.");
        setStage("error");
      });
  }, []);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.srcObject = recorder.previewStream;
  }, [questionPhase, recorder.previewStream, stage]);

  useEffect(() => {
    if (!recorder.blob) {
      setReviewVideoUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }

        return null;
      });
      return;
    }

    const url = URL.createObjectURL(recorder.blob);
    setReviewVideoUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }

      return url;
    });

    return () => URL.revokeObjectURL(url);
  }, [recorder.blob]);

  useEffect(() => {
    if (!config) {
      return;
    }

    // Reset failure state at the start of each (re)initialisation attempt
    setTurnstileLoadFailed(false);

    let scriptWaitAttempts = 0;
    // 60 ticks × 250 ms = 15 seconds max wait for the Turnstile CDN script to load.
    // Ad blockers or strict firewalls will silently prevent the script from loading;
    // without this timeout the container just sits empty with no feedback.
    const MAX_SCRIPT_WAIT = 60;

    const intervalId = window.setInterval(() => {
      if (!window.turnstile) {
        scriptWaitAttempts++;
        if (scriptWaitAttempts >= MAX_SCRIPT_WAIT) {
          window.clearInterval(intervalId);
          setTurnstileLoadFailed(true);
        }
        return;
      }

      if (!turnstileContainerRef.current || turnstileWidgetIdRef.current) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: config.turnstileSiteKey,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        // When Turnstile has an internal error the widget becomes unresponsive.
        // Force a full re-render so the user gets a fresh, clickable challenge.
        "error-callback": resetTurnstileWidget,
        theme: "light",
        size: "flexible"
      });

      window.clearInterval(intervalId);
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [config, turnstileRenderKey]);

  const questions = config?.questions ?? [];
  const currentQuestion = questions[questionIndex];
  const currentAttemptCount = currentQuestion ? attemptsByQuestion[currentQuestion.id] ?? 0 : 0;
  const displayedAttempt = Math.min(
    questionPhase === "decision" ? Math.max(currentAttemptCount, 1) : currentAttemptCount + 1,
    2
  );
  const orderedAnswers = questions
    .map((question) => recordedAnswers[question.id])
    .filter(Boolean) as RecordedAnswer[];
  const totalBytes = orderedAnswers.reduce((sum, answer) => sum + answer.blob.size, 0);

  const allQuestionsRecorded = questions.length > 0 && orderedAnswers.length === questions.length;

  function updateProfile<K extends keyof ApplicantProfile>(key: K, value: ApplicantProfile[K]) {
    setProfile((current) => ({
      ...current,
      [key]: value
    }));
  }

  async function beginInterview() {
    setErrorMessage(null);
    const prepareError = await recorder.prepare();

    if (prepareError !== null) {
      setErrorMessage(prepareError);
      return;
    }

    setStage("record");
    setQuestionIndex(0);
    setQuestionPhase("intro");
    setPhaseSecondsLeft(PREPARATION_SECONDS);
    setStatusMessage("Each question begins with a short confirmation step.");
  }

  function moveToNextQuestionOrReview() {
    recorder.reset();

    if (questionIndex < questions.length - 1) {
      setQuestionIndex((current) => current + 1);
      setQuestionPhase("intro");
      setPhaseSecondsLeft(PREPARATION_SECONDS);
      setStatusMessage("Review the instructions, then continue to the next question.");
      return;
    }

    setStage("review");
    setStatusMessage("Review your application, then submit it to Global Water Center.");
  }

  function keepCurrentAnswer() {
    if (!currentQuestion || !recorder.blob) {
      return;
    }

    const safeName = slugify(`${profile.firstName}-${profile.lastName}`) || "candidate";
    const fileName = `${safeName}-${currentQuestion.id}.webm`;
    setRecordedAnswers((current) => ({
      ...current,
      [currentQuestion.id]: {
        questionId: currentQuestion.id,
        prompt: currentQuestion.prompt,
        blob: recorder.blob!,
        fileName,
        mimeType: recorder.blob!.type || "video/webm",
        durationSeconds: recorder.durationSeconds
      }
    }));
    moveToNextQuestionOrReview();
  }

  function retryCurrentQuestion() {
    if (!currentQuestion) {
      return;
    }

    recorder.reset();
    phaseEndTimeRef.current = Date.now() + PREPARATION_SECONDS * 1000;
    setQuestionPhase("prep");
    setPhaseSecondsLeft(PREPARATION_SECONDS);
    setStatusMessage("Use the preparation time, then recording will begin automatically.");
  }

  function beginPreparationPhase() {
    phaseEndTimeRef.current = Date.now() + PREPARATION_SECONDS * 1000;
    setQuestionPhase("prep");
    setPhaseSecondsLeft(PREPARATION_SECONDS);
    setStatusMessage("Use the preparation time, then recording will begin automatically.");
  }

  function beginRecordingNow() {
    if (!currentQuestion || recorder.state === "error") {
      return;
    }

    phaseEndTimeRef.current = null;
    setAttemptsByQuestion((current) => ({
      ...current,
      [currentQuestion.id]: (current[currentQuestion.id] ?? 0) + 1
    }));
    setQuestionPhase("recording");
    setStatusMessage("Recording has started.");
    recorder.start();
  }

  async function submitApplication() {
    if (!config) {
      return;
    }

    setErrorMessage(null);

    if (!turnstileToken) {
      setErrorMessage("Please complete the verification check before submitting.");
      return;
    }

    if (!allQuestionsRecorded) {
      setErrorMessage("Each interview question needs a recorded answer.");
      return;
    }

    const oversizedAnswer = orderedAnswers.find(
      (answer) => answer.blob.size > config.maxSingleFileMb * 1024 * 1024
    );

    if (oversizedAnswer) {
      setErrorMessage(
        `One answer is larger than the ${config.maxSingleFileMb} MB limit. Re-record that answer more briefly.`
      );
      return;
    }

    if (totalBytes > config.maxTotalUploadMb * 1024 * 1024) {
      setErrorMessage(
        `This submission is ${formatMegabytes(totalBytes)} and exceeds the ${config.maxTotalUploadMb} MB total limit.`
      );
      return;
    }

    setStage("uploading");
    setStatusMessage("Submitting your interview...");

    try {
      const response = await submitApplicationRequest(
        buildSubmissionRequest({
          invitationToken: config.invitationToken,
          applicant: profile,
          questionSetId: config.questionSetId,
          turnstileToken,
          answers: orderedAnswers
        }),
        orderedAnswers
      );

      resetTurnstileWidget();
      setStage("done");
      setStatusMessage(
        response.status === "completed" ? "Submission complete. Thank you." : "Submission accepted."
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Submission failed.";
      if (message === "Spam protection validation failed.") {
        resetTurnstileWidget();
      }
      setErrorMessage(message);
      setStage("review");
      setStatusMessage(
        message === "Spam protection validation failed."
          ? "Verification expired or was not completed. Please complete it again and resubmit."
          : "Submission failed. Correct any issues and try again."
      );
    }
  }

  const profileIsComplete =
    profile.firstName.trim() &&
    profile.lastName.trim() &&
    profile.email.trim() &&
    profile.consentAccepted;

  useEffect(() => {
    if (stage !== "record" || !currentQuestion || questionPhase !== "prep") {
      return;
    }

    if (phaseSecondsLeft <= 0) {
      beginRecordingNow();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPhaseSecondsLeft((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [currentQuestion, phaseSecondsLeft, questionPhase, stage]);

  useEffect(() => {
    if (stage !== "record" || questionPhase !== "recording") {
      return;
    }

    if (recorder.durationSeconds >= MAX_RECORDING_SECONDS && recorder.state === "recording") {
      recorder.stop();
    }
  }, [questionPhase, recorder, recorder.durationSeconds, recorder.state, stage]);

  useEffect(() => {
    if (stage !== "record" || questionPhase !== "recording") {
      return;
    }

    if (recorder.state === "stopped" && recorder.blob) {
      phaseEndTimeRef.current = Date.now() + DECISION_SECONDS * 1000;
      setQuestionPhase("decision");
      setPhaseSecondsLeft(DECISION_SECONDS);
      setStatusMessage("Keep this recording or use your one remaining retry.");
    }
  }, [questionPhase, recorder.blob, recorder.state, stage]);

  useEffect(() => {
    if (stage !== "record" || questionPhase !== "decision") {
      return;
    }

    if (phaseSecondsLeft <= 0) {
      keepCurrentAnswer();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPhaseSecondsLeft((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [phaseSecondsLeft, questionPhase, stage]);

  // When the tab returns from background, browsers may have throttled or paused the
  // setTimeout-based timers. Recalculate remaining time from the wall-clock end time
  // so the timer snaps to the correct value instead of drifting or freezing.
  useEffect(() => {
    if (stage !== "record") {
      return;
    }

    function handleVisibilityChange() {
      if (document.visibilityState !== "visible" || phaseEndTimeRef.current === null) {
        return;
      }
      const remaining = Math.max(
        0,
        Math.round((phaseEndTimeRef.current - Date.now()) / 1000)
      );
      setPhaseSecondsLeft(remaining);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [stage]);

  return (
    <main className="app-shell">
      <section className="hero-panel">
        <div className="hero-brand">
          <img src={gwcLogo} alt="Global Water Center" className="hero-logo" />
          <img src={gwcSymbol} alt="" aria-hidden="true" className="hero-symbol" />
        </div>
        <p className="eyebrow">Global Water Center Interview Portal</p>
        <h1>{config?.organizationName ?? "Global Water Center"}</h1>
        <p className="lede">
          {config?.introText ??
            "Record your short interview responses below. Please complete each prompt and submit when you are finished."}
        </p>
        <div className="hero-meta">
          <span>Interview: {config?.roleTitle ?? "Loading..."}</span>
          <span>Questions: {questions.length}</span>
          <span>Video response application</span>
        </div>
      </section>

      <section className="workspace-panel">
        <header className="workspace-header">
          <div>
            <p className="section-label">Applicant workspace</p>
            <h2>
              {stage === "profile" && "Profile"}
              {stage === "record" && `Question ${questionIndex + 1} of ${questions.length}`}
              {stage === "review" && "Review and submit"}
              {stage === "uploading" && "Uploading"}
              {stage === "done" && "Completed"}
              {stage === "error" && "Unavailable"}
              {stage === "loading" && "Loading"}
            </h2>
          </div>
          <p className="status-chip">{statusMessage}</p>
        </header>

        {errorMessage ? <p className="error-banner">{errorMessage}</p> : null}

        {stage === "loading" ? <div className="card-surface shimmer" /> : null}

        {stage === "error" ? (
          <div className="stack">
            <article className="card-surface">
              <p className="section-label">Interview unavailable</p>
              <h3>This interview link cannot be used.</h3>
              <p>{errorMessage ?? "Please contact Global Water Center if you believe this is an error."}</p>
            </article>
          </div>
        ) : null}

        {stage === "profile" ? (
          <div className="stack">
            <div className="grid-two">
              <label>
                First name
                <input
                  value={profile.firstName}
                  disabled
                  autoComplete="given-name"
                />
              </label>
              <label>
                Last name
                <input
                  value={profile.lastName}
                  disabled
                  autoComplete="family-name"
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  autoComplete="email"
                />
              </label>
              <label>
                Phone
                <input
                  value={profile.phone}
                  onChange={(event) => updateProfile("phone", event.target.value)}
                  autoComplete="tel"
                />
              </label>
            </div>

            <label>
              Location
              <input
                value={profile.location}
                onChange={(event) => updateProfile("location", event.target.value)}
                placeholder="City, State or City, Country"
              />
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={profile.consentAccepted}
                onChange={(event) => updateProfile("consentAccepted", event.target.checked)}
              />
              <span>
                I confirm these details are correct and consent to Global Water Center reviewing my recorded responses.
              </span>
            </label>

            <div className="cta-row">
              <button type="button" className="primary-button" disabled={!profileIsComplete} onClick={beginInterview}>
                Continue to recording
              </button>
            </div>
          </div>
        ) : null}

        {stage === "record" && currentQuestion ? (
          <div className="stack">
            {/* Intro phase shows instructions; all other phases show the question prompt */}
            {questionPhase === "intro" ? (
              <article className="card-surface note-surface">
                <p className="section-label">Next question</p>
                <h3>Question {questionIndex + 1} of {questions.length}</h3>
                <p>When you continue, the question will appear, and your 1:00-minute preparation timer will begin.</p>
                <p>You may start recording early if you are ready, and you will have one retry after your first attempt.</p>
                <p>Remember, each response is limited to 2:00 minutes, so make your time count.</p>
                <p>Once you confirm an answer and move forward, that response will be locked in—there's no turning back!</p>
                <p>To ensure a smooth experience, please avoid refreshing or closing this page during the interview.</p>
              </article>
            ) : (
              <article className="question-card">
                <p className="question-index">Prompt {questionIndex + 1}</p>
                <div className="question-heading">
                  <img src={questionMarkLogo} alt="" aria-hidden="true" className="question-icon" />
                  <h3>{currentQuestion.prompt}</h3>
                </div>
                {currentQuestion.helperText ? <p>{currentQuestion.helperText}</p> : null}
                <div className="question-meta">
                  <span>Attempt {displayedAttempt} of 2</span>
                  <span>Answer limit: {formatSeconds(MAX_RECORDING_SECONDS)}</span>
                </div>
              </article>
            )}

            {/*
             * Camera sits between the question/instructions card and the timer card so
             * everything is visible without scrolling. The key prop is intentional and
             * critical: review-video and live-camera are structurally identical <div>s,
             * so without a key React reconciles them as the same DOM node. That leaves
             * the camera's srcObject set on the review video element, causing the browser
             * to display the live feed instead of the recording (srcObject beats src).
             * Distinct keys force a proper unmount + remount when switching branches.
             */}
            {questionPhase === "decision" && reviewVideoUrl ? (
              <div key="review-video" className="video-frame compact-frame">
                <video src={reviewVideoUrl} controls playsInline />
              </div>
            ) : (
              <div key="live-camera" className={`video-frame${questionPhase === "intro" ? " compact-frame" : ""}`}>
                <video ref={videoRef} autoPlay muted playsInline />
              </div>
            )}

            {recorder.error ? <p className="error-banner">{recorder.error}</p> : null}

            {/* Timer / status cards shown during non-intro phases */}
            {questionPhase === "prep" ? (
              <article className="card-surface note-surface">
                <p className="section-label">Preparation</p>
                <h3>{formatSeconds(phaseSecondsLeft)}</h3>
                <p>The recording will start automatically when the timer ends, or you can begin sooner.</p>
              </article>
            ) : null}

            {questionPhase === "recording" ? (
              <article className="card-surface pending-surface">
                <p className="section-label">Recording</p>
                <h3>{formatSeconds(Math.max(MAX_RECORDING_SECONDS - recorder.durationSeconds, 0))}</h3>
                <p>Time remaining. Recording stops automatically at 2:00.</p>
              </article>
            ) : null}

            {questionPhase === "decision" ? (
              <article className="card-surface note-surface">
                <p className="section-label">Keep or retry</p>
                <h3>{formatSeconds(phaseSecondsLeft)}</h3>
                <p>Keep this answer or use your remaining retry before the timer expires.</p>
                <p>If you keep it and move on, you will not be able to come back and change it later.</p>
              </article>
            ) : null}

            <div className="cta-row">
              {questionPhase === "intro" ? (
                <button type="button" className="primary-button" onClick={beginPreparationPhase}>
                  Next
                </button>
              ) : null}

              {questionPhase === "prep" ? (
                <button type="button" className="primary-button" onClick={beginRecordingNow}>
                  Start recording now
                </button>
              ) : null}

              {questionPhase === "recording" && recorder.state === "recording" ? (
                <button type="button" className="primary-button" onClick={recorder.stop}>
                  Stop recording
                </button>
              ) : null}

              {questionPhase === "decision" && recorder.state === "stopped" && recorder.blob ? (
                <>
                  {currentAttemptCount < 2 ? (
                    <button type="button" className="secondary-button" onClick={retryCurrentQuestion}>
                      One more try
                    </button>
                  ) : null}
                  <button type="button" className="primary-button" onClick={keepCurrentAnswer}>
                    Keep answer
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {stage === "review" ? (
          <div className="stack">
            <div className="summary-grid">
              <article className="card-surface">
                <p className="section-label">Applicant</p>
                <h3>
                  {profile.firstName} {profile.lastName}
                </h3>
                <p>{profile.email}</p>
                <p>{profile.location}</p>
              </article>
              <article className="card-surface">
                <p className="section-label">Recorded answers</p>
                <h3>{orderedAnswers.length}</h3>
                <p>
                  Submission size: {formatMegabytes(totalBytes)} of {config?.maxTotalUploadMb ?? 0} MB.
                </p>
              </article>
            </div>

            <div className="recording-list">
              {orderedAnswers.map((answer, index) => (
                <article key={answer.questionId} className="recording-item">
                  <div>
                    <p className="question-index">Prompt {index + 1}</p>
                    <h4>{answer.prompt}</h4>
                  </div>
                  <span>{formatSeconds(answer.durationSeconds)}</span>
                </article>
              ))}
            </div>

            <div className="card-surface">
              <p className="section-label">Verification</p>
              {turnstileLoadFailed ? (
                <div>
                  <p style={{ marginBottom: "0.75rem" }}>
                    The spam protection check could not load. This is usually caused by an ad
                    blocker or browser extension. Please disable any ad blockers for this page,
                    then click Retry.
                  </p>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => setTurnstileRenderKey((k) => k + 1)}
                  >
                    Retry verification
                  </button>
                </div>
              ) : null}
              {/* Always keep the container in the DOM so the ref stays set;
                  hide it while the load-failure message is showing */}
              <div
                ref={turnstileContainerRef}
                style={turnstileLoadFailed ? { display: "none" } : undefined}
              />
            </div>

            <article className="card-surface note-surface">
              <p className="section-label">Before you submit</p>
              <p>Your answers are now locked. Please review the summary and submit when ready.</p>
            </article>

            <div className="cta-row">
              <button type="button" className="primary-button" onClick={submitApplication}>
                Submit application
              </button>
            </div>
          </div>
        ) : null}

        {stage === "uploading" ? (
          <div className="stack">
            <article className="card-surface pending-surface">
              <p className="section-label">Submitting</p>
              <h3>Uploading your interview</h3>
              <p>Keep this tab open while your submission is uploaded.</p>
            </article>
          </div>
        ) : null}

        {stage === "done" ? (
          <div className="stack">
            <article className="card-surface success-surface">
              <p className="section-label">Submission received</p>
              <h3>Thank you for applying.</h3>
              <p>Your recorded interview has been submitted successfully.</p>
            </article>
          </div>
        ) : null}
      </section>
    </main>
  );
}
